import Joi from "joi";
import { Input } from "../form/validatedInput";
import { Select } from "../form/validatedSelect";
import { useEffect, useState } from "react";
import { RAGInput } from "./ragInput";
import { useSelector, shallowEqual } from "react-redux";
import Loader from "../global/loader";

type PropType = {
    metric: MetricDefinition,
    applyTo: { label: string, value: string }[],
    terminal: string,
    onSubmit: (metricChangeDetails: MetricEditingState) => void,
    isFallback: boolean
}

export type MetricEditingState = {
    fromDate: string,
    toDate: string,
    fromTime: string,
    toTime: string,
    value1: number,
    value2: number
}

const formatApplyToString = (groupType: string, applyTo: { label: string, value: string }[]): string => {
    return applyTo.map((lane) => lane.value.toString()).join(", ")
};

export const EditMetric: React.FC<PropType> = ({ metric, applyTo, terminal, onSubmit, isFallback }) => {
    const { validationError, submitted, submitSuccess } = useSelector(
        (state: MainState) => ({
            validationError: state.futureSecurity.validationError,
            submitted: state.futureSecurity.updatePending,
            submitSuccess: state.futureSecurity.updateSuccess
        }),
        shallowEqual
    );

    const getFormattedDate = (date: Date): string => {
        return date.toISOString().split("T")[0]; // Converts to YYYY-MM-DD
    };

    const getTomorrowsBeginning = (): Date => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        return tomorrow;
    };

    const tomorrowsBeginning = getTomorrowsBeginning();
    const dayAfterTomorrow = new Date(tomorrowsBeginning);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);

    const [minToDate, setMinToDate] = useState(getFormattedDate(tomorrowsBeginning))
    const [metricChangeDetails, setMetricChangeDetails] = useState<MetricEditingState>({
        fromDate: getFormattedDate(tomorrowsBeginning),
        toDate: getFormattedDate(dayAfterTomorrow),
        fromTime: "00",
        toTime: "00",
        value1: metric.value1,
        value2: metric.value2 ?? undefined,
    })

    useEffect(() => {
        // changes dates for toDate if fromDate changes, so dates can't be backwards
        const mtd = getFormattedDate(new Date(metricChangeDetails.fromDate))
        setMinToDate(mtd)
        if (metricChangeDetails.fromDate > metricChangeDetails.toDate) {
            setMetricChangeDetails({ ...metricChangeDetails, toDate: mtd })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metricChangeDetails.fromDate])

    const hoursOptions = Array.from({ length: 24 }, (_, i) => {
        const hour = i.toString().padStart(2, '0');

        return {
            value: hour,
            label: `${hour}:00`
        };
    });

    const hourSchema = Joi.string().required().pattern(/^([0-1]?[0-9]|2[0-3])$/);

    const formattedString = isFallback ? "Default Values" : formatApplyToString(metric.groupType, applyTo);
    const minAllowedDate = getFormattedDate(tomorrowsBeginning);

    useEffect(() => {
        setMetricChangeDetails(prev => ({
            ...prev,
            value1: metric.value1,
            value2: metric.value2 ?? undefined,
        }));
    }, [metric.value1, metric.value2]);

    useEffect(() => {
        console.log("Submit success: ", submitSuccess)
        console.log("Pending: ", submitted)
        console.log("error: ", validationError)
    }, [submitSuccess, submitted, validationError])
    return (
        <div>
            <h2>Editing {metric.airport.slice(0,3)} - {metric.name} - {`${terminal !== "main" ? `${terminal} -` : ''}`} {formattedString}</h2>
            <h3><u>{isFallback ? "Default Target" : "Target"}</u></h3>
            <RAGInput comparator={metric.comparator} metric={metricChangeDetails} onUpdate={setMetricChangeDetails} />
            {!isFallback &&
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: "20px" }}>
                        <div style={{ flex: 1, marginRight: '10px' }}>
                            <Input min={minAllowedDate} type="date" value={metricChangeDetails.fromDate} onChange={(v) => setMetricChangeDetails({ ...metricChangeDetails, fromDate: v })} schema={Joi.date()} label="From Date" />
                        </div>
                        <div style={{ flex: 1, marginRight: '10px' }}>
                            <Select
                                label="From Time (UTC)"
                                options={hoursOptions}
                                value={metricChangeDetails.fromTime}
                                schema={hourSchema}
                                onChange={(v) => setMetricChangeDetails({ ...metricChangeDetails, fromTime: v })}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1, marginRight: '10px' }}>
                            <Input min={minToDate} type="date" value={metricChangeDetails.toDate} onChange={(v) => setMetricChangeDetails({ ...metricChangeDetails, toDate: v })} schema={Joi.date()} label="To Date" />
                        </div>
                        <div style={{ flex: 1, marginRight: '10px' }}>
                            <Select
                                label="To Time (UTC)"
                                options={hoursOptions}
                                value={metricChangeDetails.toTime}
                                schema={hourSchema}
                                onChange={(v) => setMetricChangeDetails({ ...metricChangeDetails, toTime: v })}
                            />
                        </div>
                    </div>
                </>
            }
            {validationError && <p className='error-message'>{validationError}</p>}

            <button onClick={() => onSubmit(metricChangeDetails)}>Save</button>
            {submitSuccess && <p>Updated successfully</p>}
            <Loader isLoading={submitted && !validationError && !submitSuccess} />
        </div>
    );
};